import React from 'react';

const Features: React.FC = () => {
  const features = [
    {
      icon: '🎴',
      title: 'Pouvoirs spéciaux',
      description: 'AS, 8, Dame avec effets uniques qui changent le cours de la partie',
      color: 'from-red-500 to-pink-500',
      delay: '0ms'
    },
    {
      icon: '🧠',
      title: 'Stratégie et réflexes',
      description: 'Combinez vos cartes intelligemment pour prendre l\'avantage',
      color: 'from-blue-500 to-purple-500',
      delay: '100ms'
    },
    {
      icon: '🏆',
      title: 'Compétition',
      description: 'Gagnez 3 fois d\'affilée et faites baisser votre score !',
      color: 'from-yellow-500 to-orange-500',
      delay: '200ms'
    },
    {
      icon: '📊',
      title: 'Suivi des scores',
      description: 'Suivi des scores en temps réel pour suivre vos performances',
      color: 'from-green-500 to-teal-500',
      delay: '300ms'
    }
  ];

  return (
    <section id="features" className="section-padding bg-gradient-to-br from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Enhanced Section Header */}
        <div className="text-center mb-20 fade-in-up">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Fonctionnalités <span className="gradient-text">clés</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Découvrez ce qui rend Jeu 151 unique et addictif
          </p>
        </div>

        {/* Enhanced Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
          {features.map((feature, index) => (
            <div 
              key={index} 
              className="card card-hover p-8 text-center group rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-500"
              style={{ animationDelay: feature.delay }}
            >
              {/* Enhanced Icon */}
              <div className={`w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-r ${feature.color} flex items-center justify-center text-3xl group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                {feature.icon}
              </div>

              {/* Enhanced Title */}
              <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-primary-600 transition-colors duration-300">
                {feature.title}
              </h3>

              {/* Enhanced Description */}
              <p className="text-gray-600 leading-relaxed group-hover:text-gray-700 transition-colors duration-300">
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        {/* Enhanced Additional Info */}
        <div className="gradient-border p-8 rounded-3xl shadow-2xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="fade-in-up">
              <h3 className="text-3xl font-bold text-gray-900 mb-6">
                Un jeu de cartes traditionnel <span className="gradient-text">revisité</span>
              </h3>
              <p className="text-gray-700 mb-8 leading-relaxed text-lg">
                Jeu 151 ajoute des mécaniques modernes et des pouvoirs spéciaux 
                pour une expérience de jeu unique et stratégique.
              </p>
              <ul className="space-y-4 text-gray-700">
                <li className="flex items-center text-lg">
                  <span className="text-primary-600 mr-3 text-xl">✓</span>
                  Interface intuitive et moderne
                </li>
                <li className="flex items-center text-lg">
                  <span className="text-primary-600 mr-3 text-xl">✓</span>
                  Mode hors-ligne disponible
                </li>
                <li className="flex items-center text-lg">
                  <span className="text-primary-600 mr-3 text-xl">✓</span>
                  Statistiques détaillées
                </li>
              </ul>
            </div>
            <div className="card-hover bg-white rounded-2xl p-8 shadow-xl">
              <div className="text-center">
                <div className="text-5xl mb-6 animate-float">🎯</div>
                <h4 className="text-2xl font-bold text-gray-900 mb-4">
                  Objectif du jeu
                </h4>
                <p className="text-gray-600 leading-relaxed">
                  Évitez d'être le premier à dépasser 151 points en utilisant 
                  stratégiquement vos cartes et leurs pouvoirs spéciaux.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* New Game Highlights Section */}
        <div className="mt-20">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              Pourquoi choisir <span className="gradient-text">Jeu 151</span> ?
            </h3>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Une expérience de jeu unique qui combine tradition et innovation
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="card-hover bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 text-center">
              <div className="text-4xl mb-4 animate-float">⚡</div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Performance optimale</h4>
              <p className="text-gray-600">Chargement rapide et fonctionnement fluide sur tous les appareils</p>
            </div>
            
            <div className="card-hover bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-8 text-center">
              <div className="text-4xl mb-4 animate-float-staggered">🎮</div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Contrôles intuitifs</h4>
              <p className="text-gray-600">Navigation simple et gestes naturels pour une prise en main immédiate</p>
            </div>
            
            <div className="card-hover bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-8 text-center">
              <div className="text-4xl mb-4 animate-float">📱</div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Design responsive</h4>
              <p className="text-gray-600">Adaptation parfaite sur mobile, tablette et desktop</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features; 